/**
 * AssessmentDialog Component
 * Dialog for configuring and submitting impact assessments
 */

import React, { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import { withoutBackdropClose } from "../../utils/withoutBackdropClose";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import { ConceptSelector } from "./ConceptSelector";
import { KeywordInput } from "./KeywordInput";
import { useAssessmentForm } from "./useAssessmentForm";

interface AssessmentDialogProps {
  open: boolean;
  selectedRequirementIds: string[];
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export const AssessmentDialog: React.FC<AssessmentDialogProps> = ({
  open,
  selectedRequirementIds,
  onClose,
  onSuccess,
  onError,
}) => {
  const form = useAssessmentForm({
    selectedRequirementIds,
    onSuccess: (message) => {
      onSuccess(message);
      onClose();
    },
    onError,
  });

  // Track if concepts have been loaded to prevent infinite loop
  const hasLoadedRef = useRef(false);

  // Confirmation dialog state for closing during submission
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Load concepts when dialog opens (only once)
  useEffect(() => {
    console.log(
      "🔄 [AssessmentDialog] useEffect triggered - open:",
      open,
      "hasLoaded:",
      hasLoadedRef.current,
    );
    if (open && !hasLoadedRef.current) {
      console.log(
        "🚀 [AssessmentDialog] Calling form.loadConcepts() - FIRST TIME",
      );
      hasLoadedRef.current = true;
      form.loadConcepts();
    } else if (open && hasLoadedRef.current) {
      console.log(
        "⏭️  [AssessmentDialog] Skipping loadConcepts - already loaded",
      );
    }

    // Reset flag when dialog closes
    if (!open) {
      console.log(
        "🔒 [AssessmentDialog] Dialog closed, resetting hasLoaded flag",
      );
      hasLoadedRef.current = false;
    }
  }, [open]);

  const handleSubmit = async () => {
    console.log("🔘 [AssessmentDialog] Assess button clicked");
    const success = await form.submitAssessment();
    console.log("📝 [AssessmentDialog] Submit result:", success);
    if (success) {
      console.log("✅ [AssessmentDialog] Success, closing dialog");
      onClose();
    }
  };

  const handleClose = () => {
    // If submitting, show confirmation dialog
    if (form.isSubmitting) {
      setShowCancelConfirm(true);
      return;
    }
    form.resetForm();
    onClose();
  };

  const handleConfirmCancel = () => {
    setShowCancelConfirm(false);
    form.abortSubmission();
    form.resetForm();
    onClose();
  };

  const handleCancelClose = () => {
    setShowCancelConfirm(false);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={withoutBackdropClose(handleClose)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        disableEscapeKeyDown={form.isSubmitting}
      >
        <DialogTitle>Configure Assessment</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select concept from below dimensions or enter keywords to include in
            the impact analysis.
          </Typography>

          {form.isLoadingConcepts ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading concepts...
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexDirection: { xs: "column", md: "row" },
                }}
              >
                {/* Left column: Four concept selectors */}
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {/* Product Type */}
                  <ConceptSelector
                    label="Product Type"
                    options={form.productTypes}
                    value={form.selectedProductTypes}
                    loading={form.loadingProductTypes}
                    tooltipText="Select no more than 5 values."
                    otherSelectionsCount={
                      form.selectedIngredients.length +
                      form.selectedTherapeuticAreas.length +
                      form.selectedDepartments.length
                    }
                    onInputChange={form.handleProductTypeSearch}
                    onChange={form.setSelectedProductTypes}
                  />

                  {/* Ingredient */}
                  <ConceptSelector
                    label="Ingredient"
                    options={form.ingredients}
                    value={form.selectedIngredients}
                    loading={form.loadingIngredients}
                    tooltipText="Select no more than 5 values."
                    otherSelectionsCount={
                      form.selectedProductTypes.length +
                      form.selectedTherapeuticAreas.length +
                      form.selectedDepartments.length
                    }
                    onInputChange={form.handleIngredientSearch}
                    onChange={form.setSelectedIngredients}
                  />

                  {/* Therapeutic Area */}
                  <ConceptSelector
                    label="Therapeutic Area"
                    options={form.therapeuticAreas}
                    value={form.selectedTherapeuticAreas}
                    loading={form.loadingTherapeuticAreas}
                    tooltipText="Select no more than 5 values."
                    otherSelectionsCount={
                      form.selectedProductTypes.length +
                      form.selectedIngredients.length +
                      form.selectedDepartments.length
                    }
                    onInputChange={form.handleTherapeuticAreaSearch}
                    onChange={form.setSelectedTherapeuticAreas}
                  />

                  {/* Department */}
                  <ConceptSelector
                    label="Department"
                    options={form.departments}
                    value={form.selectedDepartments}
                    loading={form.loadingDepartments}
                    tooltipText="Select no more than 5 values."
                    otherSelectionsCount={
                      form.selectedProductTypes.length +
                      form.selectedIngredients.length +
                      form.selectedTherapeuticAreas.length
                    }
                    onInputChange={form.handleDepartmentSearch}
                    onChange={form.setSelectedDepartments}
                  />
                </Box>

                {/* Right column: Keywords */}
                <Box sx={{ flex: 1 }}>
                  <KeywordInput
                    value={form.keywords}
                    onChange={form.setKeywords}
                    tooltipText="Enter keywords separated by commas. Max. 50 characters."
                  />
                </Box>
              </Box>

              {/* Tip */}
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Tip:</strong> Choose at least one value from the
                  four dimensions or enter at least one keyword to proceed.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={form.isSubmitting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !form.hasAtLeastOneValue ||
              !form.isValidSelection ||
              form.isSubmitting
            }
            startIcon={
              form.isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
            sx={{ textTransform: "none" }}
          >
            {form.isSubmitting ? "Processing..." : "Assess"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for canceling during submission */}
      <Dialog
        open={showCancelConfirm}
        onClose={withoutBackdropClose(handleCancelClose)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel Submission?</DialogTitle>
        <DialogContent>
          <Typography>Submission is in progress. Cancel and close?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelClose} variant="outlined" sx={{ textTransform: "none" }}>
            No, Continue
          </Button>
          <Button
            onClick={handleConfirmCancel}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
